import { createFileRoute, Link } from "@tanstack/react-router";
import { RangeMapAnimation } from "@/components/RangeMapAnimation";
import { SOLD_STATES } from "@/data/soldStates";

export const Route = createFileRoute("/range-map")({
  head: () => ({
    meta: [
      { title: "Range Map | Coyote Creek Hat Co." },
      { name: "description", content: "See where Coyote Creek hats have ridden across the country." },
      { property: "og:title", content: "Range Map | Coyote Creek Hat Co." },
      { property: "og:description", content: "See every state Coyote Creek hats have shipped to." },
      { property: "og:url", content: "https://coyotecreekhatco.com/range-map" },
    ],
    links: [{ rel: "canonical", href: "https://coyotecreekhatco.com/range-map" }],
  }),
  component: RangeMapPage,
});

function RangeMapPage() {
  const sortedStates = [...SOLD_STATES].sort();

  return (
    <div>
      {/* Hero */}
      <div className="relative w-full h-[50vh] min-h-[320px] overflow-hidden">
        <img
          src="https://cdn.shopify.com/s/files/1/0808/1332/6620/files/IMG_9396.jpg?v=1706758562"
          alt="Range Map"
          className="absolute inset-0 h-full w-full object-cover"
          width={1920}
          height={768}
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="font-heading text-5xl md:text-7xl uppercase tracking-[0.15em] text-white text-center px-6 drop-shadow-lg">
            Range Map
          </h1>
        </div>
      </div>

      {/* Animated map section */}
      <section className="bg-background py-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="text-center">
            <p className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-saddle">
              From Houston, Across the Country
            </p>
            <h2 className="mt-4 font-heading text-4xl md:text-5xl font-black uppercase tracking-[0.05em] text-primary">
              Shipped to{" "}
              <span className="text-[#FA5021]">{SOLD_STATES.length} states</span>
              <span className="text-primary"> and counting</span>
            </h2>
            <p className="mx-auto mt-6 max-w-2xl font-body text-base leading-relaxed text-muted-foreground md:text-lg">
              Every order lights up another corner of the map. Watch where Coyote
              Creek has ridden — and where it's headed next.
            </p>
          </div>

          <div className="mt-12">
            <RangeMapAnimation />
          </div>

          {/* State list */}
          <div className="mt-16">
            <span className="block text-center font-ui text-xs font-bold uppercase tracking-[0.3em] text-saddle">
              The Range
            </span>
            <h3 className="mt-3 text-center font-heading text-2xl font-black uppercase tracking-tight text-primary md:text-3xl">
              Where We've Ridden
            </h3>
            <div className="mx-auto mt-6 h-1 w-12 bg-[#FA5021]" />
            <ul className="mx-auto mt-10 grid max-w-4xl grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {sortedStates.map((state) => (
                <li
                  key={state}
                  className="flex items-center gap-2 font-body text-sm font-medium text-primary"
                >
                  <span className="inline-block h-2 w-2 rounded-full bg-[#FA5021] shadow-[0_0_8px_rgba(250,80,33,0.6)]" />
                  {state}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-1 items-center gap-10 border-t border-primary/15 pt-16 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-7">
              <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/60">
                Plant Your Flag
              </span>
              <h2 className="mt-4 font-heading text-3xl font-black uppercase tracking-tight text-primary md:text-5xl">
                Don't see your state?
              </h2>
              <div className="mt-6 h-1 w-12" style={{ backgroundColor: "#FA5021" }} />
              <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-primary/70 md:text-lg">
                Be the first in your state to rep Coyote Creek — and help us expand the range one hat at a time.
              </p>
            </div>
            <div className="md:col-span-5 md:text-right">
              <Link to="/contact" className="btn-cc-accent tracking-[0.2em]">
                Get In Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
